/**
 * OnboardingProvider.js - V1 Commercial Readiness Remediation
 * 
 * Context provider for onboarding state
 * Shows welcome wizard for FIRST LOGIN ONLY
 * 
 * V1 COMPLETION: Now uses backend persistence instead of localStorage
 * Wizard only appears once per user, regardless of device/browser
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import WelcomeWizard from './WelcomeWizard';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';

const OnboardingContext = createContext({
  showWizard: false,
  setShowWizard: () => {},
  hasCompletedOnboarding: false,
  completeOnboarding: () => {},
  resetOnboarding: () => {},
});

export const useOnboarding = () => useContext(OnboardingContext);

export const OnboardingProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Check if user needs onboarding - BACKEND FIRST, then localStorage fallback
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.id) return;
      
      try {
        // Try to get preferences from backend (persistent across devices)
        const response = await api.getAuthUserPreferences();
        const prefs = response.data;
        
        if (prefs.onboarding_completed) {
          // Backend says completed - don't show wizard
          setHasCompletedOnboarding(true);
          setShowWizard(false);
          // Also update localStorage for consistency
          localStorage.setItem(`scopeops_onboarding_${user.id}`, JSON.stringify({
            dismissed: true,
            completedAt: prefs.onboarding_dismissed_at
          }));
        } else {
          // Backend says not completed - check if this is first login
          // Show wizard only once
          setHasCompletedOnboarding(false);
          setShowWizard(true);
        }
      } catch (e) {
        // Backend unavailable - fall back to localStorage
        console.warn('Could not fetch user preferences, using localStorage fallback:', e);
        const storageKey = `scopeops_onboarding_${user.id}`;
        const saved = localStorage.getItem(storageKey);
        
        if (saved) {
          try {
            const data = JSON.parse(saved);
            setHasCompletedOnboarding(data.dismissed || false);
            setShowWizard(!data.dismissed);
          } catch (parseError) {
            setHasCompletedOnboarding(false);
            setShowWizard(true);
          }
        } else {
          // No saved data - first time user
          setHasCompletedOnboarding(false);
          setShowWizard(true);
        }
      }
      
      setPreferencesLoaded(true);
    };

    checkOnboardingStatus();
  }, [user?.id]);

  // Complete onboarding - persist to backend
  const completeOnboarding = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      // Persist to backend (survives across devices/browsers)
      await api.updateAuthUserPreferences({ onboarding_completed: true });
    } catch (e) {
      console.warn('Could not save preferences to backend:', e);
    }
    
    // Also save to localStorage for immediate effect
    localStorage.setItem(`scopeops_onboarding_${user.id}`, JSON.stringify({
      dismissed: true,
      completedAt: new Date().toISOString()
    }));
    
    setHasCompletedOnboarding(true);
    setShowWizard(false);
  }, [user?.id]);

  // Reset onboarding (for testing/support purposes only)
  const resetOnboarding = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      await api.updateAuthUserPreferences({ onboarding_completed: false });
    } catch (e) {
      console.warn('Could not reset preferences in backend:', e);
    }
    
    localStorage.removeItem(`scopeops_onboarding_${user.id}`);
    setHasCompletedOnboarding(false);
    setShowWizard(true);
  }, [user?.id]);

  return (
    <OnboardingContext.Provider value={{ 
      showWizard, 
      setShowWizard, 
      hasCompletedOnboarding,
      completeOnboarding,
      resetOnboarding
    }}>
      {children}
      {/* Only show wizard when user is authenticated, has loaded preferences, and wizard should be shown */}
      {isAuthenticated && showWizard && preferencesLoaded && <WelcomeWizard onComplete={completeOnboarding} />}
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
